import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';

import type { Toast } from 'primereact/toast';
import type { Task } from 'types/interfaces';
import type {
  NewTaskAssignmentResult,
  TaskAssignmentResult,
  TaskAssignmentRouteError
} from 'types/interfaces/TaskAssignmentResult';
import { normalizeApiError } from 'utils/taskCard/taskAssignmentErrors';

import { TaskService } from 'components/services/TaskService';

import {
  buildFailedTaskAssignmentRows,
  FailedTaskAssignmentRow
} from '../lib/assignmentErrors';

function extractAssignmentResults(value: unknown): TaskAssignmentResult[] {
  if (Array.isArray(value)) return value;
  if (
    value &&
    typeof value === 'object' &&
    Array.isArray((value as { result?: unknown }).result)
  ) {
    return (value as { result: TaskAssignmentResult[] }).result;
  }
  return [];
}

function readRoutesField<T>(value: unknown, key: 'result' | 'errors'): T[] {
  if (
    value &&
    typeof value === 'object' &&
    Array.isArray((value as Record<string, unknown>)[key])
  ) {
    return (value as Record<string, T[]>)[key];
  }
  return [];
}

export function useAutoEmployeeDistribution({
  visible,
  tasks,
  toastRef,
  travelTimeOptimization = false,
  departmentId,
  date
}: {
  visible: boolean;
  tasks: Task[];
  toastRef: RefObject<Toast | null>;
  travelTimeOptimization?: boolean;
  departmentId?: number;
  date?: string;
}) {
  const [autoByTaskId, setAutoByTaskId] = useState<Record<number, boolean>>({});
  const [resultDialogVisible, setResultDialogVisible] = useState(false);
  const [results, setResults] = useState<TaskAssignmentResult[]>([]);
  const [routes, setRoutes] = useState<NewTaskAssignmentResult[]>([]);
  const [failedTasks, setFailedTasks] = useState<FailedTaskAssignmentRow[]>([]);
  const [isDistributing, setIsDistributing] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setAutoByTaskId({});
  }, [visible]);

  const hasAnySelected = useMemo(
    () => tasks.some((t) => autoByTaskId[Number(t.id)]),
    [tasks, autoByTaskId]
  );

  const setSelection = useCallback((next: Record<number, boolean>) => {
    setAutoByTaskId(next);
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (tasks.length === 0) return;
    const allSelected = tasks.every((t) => autoByTaskId[Number(t.id)]);
    setAutoByTaskId(
      allSelected
        ? {}
        : Object.fromEntries(tasks.map((t) => [Number(t.id), true]))
    );
  }, [tasks, autoByTaskId]);

  const hasValidTravelTimeParams =
    typeof departmentId === 'number' &&
    !isNaN(departmentId) &&
    typeof date === 'string' &&
    date !== '';

  const runAssignment = useCallback(
    (taskIds: number[], validationOnly: boolean) => {
      if (travelTimeOptimization) {
        return TaskService.AutoTaskEmployeeAssignmentByRoutes({
          taskIds,
          validationOnly,
          departmentId: departmentId as number,
          date: date as string
        });
      }
      return TaskService.AutoTaskEmployeeAssignment(taskIds, validationOnly);
    },
    [travelTimeOptimization, departmentId, date]
  );

  const distributeSelected = useCallback(async () => {
    if (!hasAnySelected) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Распределение',
        detail: 'Отметьте наряды для автораспределения',
        life: 4000
      });
      return;
    }
    if (travelTimeOptimization && !hasValidTravelTimeParams) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Распределение',
        detail:
          'Для распределения с учетом времени доезда выберите участок и день',
        life: 4000
      });
      return;
    }
    const selectedIds = tasks
      .filter((t) => autoByTaskId[Number(t.id)])
      .map((t) => Number(t.id))
      .filter((n) => !isNaN(n));

    setIsDistributing(true);

    try {
      const resp = await runAssignment(selectedIds, true);
      if (resp.status === 400) {
        const error = normalizeApiError(resp.data);
        const failures: TaskAssignmentRouteError[] = error
          ? selectedIds.map((taskId) => ({ taskId, errors: [error] }))
          : [];
        if (travelTimeOptimization) {
          setRoutes([]);
          setFailedTasks(buildFailedTaskAssignmentRows(failures, tasks));
        } else {
          setResults(failures);
        }
        setResultDialogVisible(true);
        return;
      }

      const value = resp?.data?.value;
      if (travelTimeOptimization) {
        setRoutes(readRoutesField<NewTaskAssignmentResult>(value, 'result'));
        setFailedTasks(
          buildFailedTaskAssignmentRows(
            readRoutesField<TaskAssignmentRouteError>(value, 'errors'),
            tasks
          )
        );
      } else {
        setResults(extractAssignmentResults(value));
      }
      setResultDialogVisible(true);
    } catch (e: any) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Распределение',
        detail: e?.message ?? 'Не удалось выполнить автораспределение',
        life: 5000
      });
    } finally {
      setIsDistributing(false);
    }
  }, [
    hasAnySelected,
    travelTimeOptimization,
    hasValidTravelTimeParams,
    runAssignment,
    tasks,
    autoByTaskId,
    toastRef
  ]);

  const clearSelection = useCallback(() => setAutoByTaskId({}), []);

  const applyAssignment = useCallback(
    (taskIds: number[]) => runAssignment(taskIds, false),
    [runAssignment]
  );

  return {
    autoByTaskId,
    setSelection,
    hasAnySelected,
    toggleSelectAll,
    distributeSelected,
    applyAssignment,
    clearSelection,
    isDistributing,
    resultDialog: {
      visible: resultDialogVisible,
      setVisible: setResultDialogVisible,
      results,
      routes,
      failedTasks
    }
  };
}
